import { Component } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { JobService } from '../../../services/job.service';
import { SettingsService } from '../../../services/settings.service';

@Component({
  selector: 'app-job-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './job-form.component.html',
  styleUrl: './job-form.component.scss'
})

export class JobFormComponent {
  jobForm!: FormGroup;
  isEdit = false;
  loading = false;
  errorMsg = '';
  currentJob: any = null;

  statusOptions: any[] = [];
  positionOptions: any[] = [];

  constructor(
    private fb: FormBuilder,
    private jobService: JobService,
    private router: Router,
    private route: ActivatedRoute,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const currentDate = `${yyyy}-${mm}-${dd}`;

    this.jobForm = this.fb.group({
      client_code: [{ value: '', disabled: true }],
      client_name: ['', Validators.required],
      job_title: ['', Validators.required],
      description: [''],
      employment_type: ['', Validators.required],
      Agreed_Hours: [null],
      posted_date: [{ value: currentDate, disabled: true }],
      closing_date: [{ value: '', disabled: true }],
      status: [{ value: 'New', disabled: true }]
    });

    // Fetch status and position options from settings
    this.settingsService.getSettings().subscribe({
      next: (settings: any) => {
        const settingsArr = Array.isArray(settings) ? settings : (settings.data || []);
        this.statusOptions = settingsArr.filter((s: any) => s.code === 'status');
        this.positionOptions = settingsArr.filter((s: any) => s.code === 'position');
      },
      error: () => {
        this.statusOptions = [];
        this.positionOptions = [];
      }
    });

    // Handle edit mode: patch form with job state if present
    const job = history.state.job;
    if (job) {
      this.isEdit = true;
      this.currentJob = job;
      this.jobForm.patchValue({
        client_code: job.client_code,
        client_name: job.client_name,
        job_title: job.job_title,
        description: job.description,
        employment_type: job.employment_type,
        Agreed_Hours: job.Agreed_Hours,
        posted_date: job.posted_date,
        closing_date: job.closing_date,
        status: job.status
      });
      // Disable fields for edit mode
      this.jobForm.get('client_code')?.disable();
      this.jobForm.get('client_name')?.disable();
      this.jobForm.get('job_title')?.disable();
      this.jobForm.get('posted_date')?.disable();
      this.jobForm.get('closing_date')?.disable();
    } else {
      // If not editing, patch from query params and disable client fields if present
      this.route.queryParams.subscribe(params => {
        if (params['client_code']) {
          this.jobForm.get('client_code')?.setValue(params['client_code']);
          this.jobForm.get('client_code')?.disable();
        }
        if (params['client_name']) {
          this.jobForm.get('client_name')?.setValue(params['client_name']);
          this.jobForm.get('client_name')?.disable();
        }
      });
    }
  }

  onSubmit() {
    if (this.jobForm.invalid) return;
    this.loading = true;
    this.errorMsg = '';
    // Use getRawValue() to include disabled fields
    const formValue = { ...this.jobForm.getRawValue() };
    // Clean up nulls for optional number fields
    if (formValue.employee_id === null || formValue.employee_id === '') formValue.employee_id = undefined;
    if (formValue.Agreed_Hours === null || formValue.Agreed_Hours === '') formValue.Agreed_Hours = undefined;

    if (this.isEdit && history.state.job && history.state.job.job_code) {
      // Edit mode: update existing job
      formValue.job_code = history.state.job.job_code;
      this.jobService.updateJob(formValue).subscribe({
        next: () => {
          this.loading = false;
          // Redirect to clients with client_code as query param
          this.router.navigate(['/clients'], { queryParams: { client_code: formValue.client_code } });
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = err?.error?.message || 'Failed to update job.';
        }
      });
    } else {
      // Create mode: create new job
      this.jobService.createJob(formValue).subscribe({
        next: () => {
          this.loading = false;
          // Redirect to clients with client_code as query param
          this.router.navigate(['/clients'], { queryParams: { client_code: formValue.client_code } });
        },
        error: (err) => {
          this.loading = false;
          this.errorMsg = err?.error?.message || 'Failed to create job.';
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/clients']);
  }
}
